import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, CreditCard, Eye, EyeOff, Minus, MoreHorizontal, Pencil, Plus, Users } from 'lucide-react';
import { listPlans, planImpact, updatePlan } from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import type { AdminPlan } from '@/lib/api/types';
import { formatMoney, formatNumber, plural } from '@/lib/format';
import { useDebounced, useUrlParam } from '@/lib/hooks';
import { Badge } from '@/components/ui/badge';
import { Button, IconButton } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState, QueryError, Skeleton } from '@/components/ui/feedback';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/menu';
import { PageHeader, Segmented } from '@/components/ui/page';
import { FilterSelect } from '@/components/ui/input';
import { TableToolbar } from '@/components/ui/toolbar';
import { Table, Td, Th, Tr } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import { PlanEditor } from './plan-editor';
import { OnlinePaymentsCard } from './online-payments-card';
import { FEATURE_KEYS, FEATURE_LABEL, LIMIT_KEYS, LIMIT_LABEL, yearlyDiscount } from './plan-form';

/**
 * The price list, edited in place. Two views of the same plans: cards, one
 * per plan, and a matrix that lines every limit and feature up in columns —
 * the view for "what does Pro have that Starter lacks".
 */
export function PlansPage() {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ['admin', 'plans'], queryFn: listPlans });
  const [view, setView] = useUrlParam('view', 'cards');
  const [editId, setEditId] = useUrlParam('edit');
  const [creating, setCreating] = useState(false);
  const [template, setTemplate] = useState<AdminPlan | null>(null);
  const [q, setQ] = useUrlParam('q');
  const [status, setStatus] = useUrlParam('status', 'all');
  const [order, setOrder] = useUrlParam('order', 'price');
  const query = useDebounced(q.trim().toLowerCase(), 150);
  const filtersActive = !!q || status !== 'all';

  const sorted = useMemo(() => {
    let rows = plans.data ?? [];
    if (query) rows = rows.filter((p) => `${p.name} ${p.code}`.toLowerCase().includes(query));
    if (status !== 'all') rows = rows.filter((p) => p.isActive === (status === 'active'));
    const by: Record<string, (a: AdminPlan, b: AdminPlan) => number> = {
      price: (a, b) => a.priceMonthly - b.priceMonthly,
      subscribers: (a, b) => b.subscriberCount - a.subscriberCount,
      name: (a, b) => a.name.localeCompare(b.name, 'ru'),
    };
    return [...rows].sort((a, b) => Number(b.isActive) - Number(a.isActive) || (by[order] ?? by.price)(a, b));
  }, [plans.data, query, status, order]);
  const editing = editId ? (plans.data ?? []).find((p) => p.id === editId) ?? null : null;
  const subscribers = (plans.data ?? []).reduce((s, p) => s + p.subscriberCount, 0);

  // Hide/show is a save like any other: the dry run first, then the flag.
  const visibility = useMutation({
    mutationFn: async ({ plan, isActive }: { plan: AdminPlan; isActive: boolean }) => {
      await planImpact(plan.id, { limits: plan.limits, features: plan.features });
      return updatePlan(plan.id, { isActive });
    },
    onSuccess: (_, v) => { toast('success', v.isActive ? 'Тариф показан' : 'Тариф скрыт', v.plan.name); qc.invalidateQueries({ queryKey: ['admin', 'plans'] }); },
    onError: (err) => toast('error', 'Не удалось изменить', describeError(err)),
  });

  const actions = (p: AdminPlan) => (
    <Menu trigger={({ toggle }) => <IconButton size="sm" title="Действия" onClick={toggle}><MoreHorizontal size={15} /></IconButton>}>
      <MenuItem icon={<Pencil size={14} />} onClick={() => setEditId(p.id)}>Изменить</MenuItem>
      <MenuItem icon={<Copy size={14} />} onClick={() => setTemplate(p)}>Создать копию</MenuItem>
      <MenuSeparator />
      <MenuItem icon={p.isActive ? <EyeOff size={14} /> : <Eye size={14} />} onClick={() => visibility.mutate({ plan: p, isActive: !p.isActive })}>
        {p.isActive ? 'Скрыть со страницы тарифов' : 'Показать на странице тарифов'}
      </MenuItem>
    </Menu>
  );

  return (
    <>
      <PageHeader
        title="Тарифы"
        subtitle={plans.data ? <>{plural(plans.data.length, ['тариф', 'тарифа', 'тарифов'])} · {plural(subscribers, ['подписка', 'подписки', 'подписок'])} · скрытый тариф исчезает со страницы тарифов, подписчики остаются на нём</> : undefined}
        action={(
          <>
            <Segmented ariaLabel="Вид" value={view === 'matrix' ? 'matrix' : 'cards'} onChange={setView} options={[{ value: 'cards', label: 'Карточки' }, { value: 'matrix', label: 'Сравнение' }]} />
            <Button icon={<Plus size={16} />} onClick={() => setCreating(true)}>Новый тариф</Button>
          </>
        )}
      />

      <OnlinePaymentsCard />

      {plans.data && plans.data.length > 0 && (
        <TableToolbar search={{ value: q, onChange: setQ, placeholder: 'Название или код' }} shown={sorted.length} total={plans.data.length} onReset={filtersActive ? () => { setQ(''); setStatus('all'); } : null}>
          <FilterSelect ariaLabel="Статус" value={status} onChange={setStatus} options={[{ value: 'all', label: 'Все тарифы' }, { value: 'active', label: 'Активные' }, { value: 'hidden', label: 'Скрытые' }]} />
          <FilterSelect ariaLabel="Порядок" value={order} onChange={setOrder} options={[{ value: 'price', label: 'По цене' }, { value: 'subscribers', label: 'По подпискам' }, { value: 'name', label: 'По названию' }]} />
        </TableToolbar>
      )}

      {plans.isPending && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-64" />)}</div>}
      {plans.isError && <QueryError error={plans.error} onRetry={() => plans.refetch()} />}
      {plans.data && plans.data.length === 0 && <EmptyState icon={<CreditCard size={28} />} title="Тарифов нет" action={<Button size="sm" onClick={() => setCreating(true)}>Создать первый</Button>} />}

      {plans.data && plans.data.length > 0 && sorted.length === 0 && <EmptyState icon={<CreditCard size={28} />} title="Ничего не подошло под фильтры" action={<Button size="sm" variant="ghost" onClick={() => { setQ(''); setStatus('all'); }}>Сбросить фильтры</Button>} />}

      {sorted.length > 0 && view !== 'matrix' && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sorted.map((p) => <PlanCard key={p.id} plan={p} share={subscribers ? p.subscriberCount / subscribers : 0} onEdit={() => setEditId(p.id)} actions={actions(p)} />)}
        </div>
      )}

      {sorted.length > 0 && view === 'matrix' && (
        <Table>
          <thead>
            <tr>
              <Th className="w-48"> </Th>
              {sorted.map((p) => (
                <th key={p.id} className="sticky top-0 z-[1] min-w-[150px] border-b border-line bg-surface px-4 py-2.5 text-left">
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="text-sm font-bold text-ink">{p.name}</div>
                      <div className="font-mono text-[11px] font-medium text-muted">{p.code}{!p.isActive && ' · скрыт'}</div>
                    </div>
                    <span className="ml-auto">{actions(p)}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Tr><Td className="text-xs font-semibold text-muted">В месяц</Td>{sorted.map((p) => <Td key={p.id} className="font-semibold tabular-nums">{formatMoney(p.priceMonthly, p.currency)}</Td>)}</Tr>
            <Tr><Td className="text-xs font-semibold text-muted">В год</Td>{sorted.map((p) => { const d = yearlyDiscount(p.priceMonthly, p.priceYearly); return <Td key={p.id} className="tabular-nums">{formatMoney(p.priceYearly, p.currency)}{d ? <span className="ml-1 text-xs text-ok">−{d} %</span> : null}</Td>; })}</Tr>
            <Tr><Td className="text-xs font-semibold text-muted">Подписок</Td>{sorted.map((p) => <Td key={p.id} className="tabular-nums">{formatNumber(p.subscriberCount)}</Td>)}</Tr>
            {LIMIT_KEYS.map((k) => (
              <Tr key={k}><Td className="text-xs font-semibold text-muted">{LIMIT_LABEL[k]}</Td>{sorted.map((p) => <Td key={p.id} className="tabular-nums">{p.limits[k] === null ? <span className="text-muted">∞</span> : formatNumber(p.limits[k])}</Td>)}</Tr>
            ))}
            {FEATURE_KEYS.map((k) => (
              <Tr key={k}><Td className="text-xs font-semibold text-muted">{FEATURE_LABEL[k]}</Td>{sorted.map((p) => <Td key={p.id}>{p.features[k] ? <Check size={16} className="text-ok" aria-label="есть" /> : <Minus size={16} className="text-faint" aria-label="нет" />}</Td>)}</Tr>
            ))}
          </tbody>
        </Table>
      )}

      {editing && <PlanEditor plan={editing} onClose={() => setEditId('')} />}
      {creating && <PlanEditor plan={null} onClose={() => setCreating(false)} />}
      {template && <PlanEditor plan={null} template={template} onClose={() => setTemplate(null)} />}
    </>
  );
}

function PlanCard({ plan, share, onEdit, actions }: { plan: AdminPlan; share: number; onEdit: () => void; actions: React.ReactNode }) {
  const enabled = FEATURE_KEYS.filter((k) => plan.features[k]);
  const discount = yearlyDiscount(plan.priceMonthly, plan.priceYearly);
  return (
    <Card className={!plan.isActive ? 'opacity-75' : undefined}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-bold text-ink">{plan.name}</h2>
            <Badge mono>{plan.code}</Badge>
            {!plan.isActive && <Badge tone="warn" dot>Скрыт</Badge>}
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted">
            <Users size={12} />{plural(plan.subscriberCount, ['подписка', 'подписки', 'подписок'])}
            {share > 0 && <span className="text-faint">· {Math.round(share * 100)} % всех</span>}
          </p>
        </div>
        <IconButton size="sm" title="Изменить тариф" onClick={onEdit}><Pencil size={14} /></IconButton>
        {actions}
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-2xl font-bold tracking-tight text-ink tabular-nums">{formatMoney(plan.priceMonthly, plan.currency)}</span>
        <span className="text-xs text-muted">в месяц</span>
      </div>
      <p className="text-xs text-muted">{formatMoney(plan.priceYearly, plan.currency)} в год{discount ? <span className="ml-1 font-semibold text-ok">−{discount} %</span> : null}</p>

      <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface-3"><div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(2, share * 100)}%` }} /></div>

      <dl className="mt-4 grid grid-cols-4 gap-2 border-t border-line pt-3">
        {LIMIT_KEYS.map((k) => (
          <div key={k}><dt className="text-[11px] text-faint">{LIMIT_LABEL[k]}</dt><dd className="text-sm font-semibold text-ink tabular-nums">{plan.limits[k] === null ? '∞' : formatNumber(plan.limits[k])}</dd></div>
        ))}
      </dl>

      <ul className="mt-3 space-y-1">
        {FEATURE_KEYS.map((k) => (
          <li key={k} className={`flex items-center gap-2 text-xs ${plan.features[k] ? 'text-ink' : 'text-faint line-through decoration-faint/60'}`}>
            {plan.features[k] ? <Check size={13} className="text-ok" /> : <Minus size={13} />}{FEATURE_LABEL[k]}
          </li>
        ))}
      </ul>
      {enabled.length === 0 && <p className="mt-2 text-[11px] text-faint">Без дополнительных возможностей</p>}
    </Card>
  );
}
